/**
 * Centralised TanStack Query key factory.
 *
 * Keys mirror the URL hierarchy — tenant first, hotel second, resource third —
 * so the cache stays partitioned per tenant/hotel. Tenant/hotel-owned server
 * state MUST include the resolved tenant/hotel UUIDs (never slugs). This is the
 * single source of key shapes; do not hand-build keys in feature code.
 */
export type Filters = Record<string, unknown> | undefined;

const hotelBase = (tenantId: string, hotelId: string) =>
  ['tenant', tenantId, 'hotel', hotelId] as const;

export const queryKeys = {
  slugResolution: (tenantSlug: string, hotelSlug: string) =>
    ['tenant-slug', tenantSlug, 'hotel-slug', hotelSlug] as const,

  me: () => ['me'] as const,

  // --- hotel-scoped lists ---
  // List keys end in `filters ?? null`, so a no-filters key is NOT a prefix of
  // a filtered one. Invalidations that must hit every filter variant of a list
  // use the matching `*Prefix` key below instead of the list key itself.
  calls: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'calls', filters ?? null] as const,
  callsPrefix: (t: string, h: string) => [...hotelBase(t, h), 'calls'] as const,
  call: (t: string, h: string, callId: string) => [...hotelBase(t, h), 'call', callId] as const,
  callTranscript: (t: string, h: string, callId: string) =>
    [...hotelBase(t, h), 'call', callId, 'transcript'] as const,
  callSummary: (t: string, h: string, callId: string) =>
    [...hotelBase(t, h), 'call', callId, 'summary'] as const,
  callToolCalls: (t: string, h: string, callId: string) =>
    [...hotelBase(t, h), 'call', callId, 'tool-calls'] as const,

  reservations: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'reservations', filters ?? null] as const,
  reservationsPrefix: (t: string, h: string) => [...hotelBase(t, h), 'reservations'] as const,
  reservation: (t: string, h: string, id: string) =>
    [...hotelBase(t, h), 'reservation', id] as const,
  reservationOffer: (t: string, h: string, offerId: string) =>
    [...hotelBase(t, h), 'offer', offerId] as const,

  bookings: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'bookings', filters ?? null] as const,
  bookingsPrefix: (t: string, h: string) => [...hotelBase(t, h), 'bookings'] as const,
  booking: (t: string, h: string, id: string) => [...hotelBase(t, h), 'booking', id] as const,

  knowledge: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'knowledge', filters ?? null] as const,
  knowledgeDocument: (t: string, h: string, id: string) =>
    [...hotelBase(t, h), 'knowledge', id] as const,
  faqs: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'faqs', filters ?? null] as const,
  faqsPrefix: (t: string, h: string) => [...hotelBase(t, h), 'faqs'] as const,
  knowledgeGaps: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'knowledge-gaps', filters ?? null] as const,
  knowledgeGapsPrefix: (t: string, h: string) => [...hotelBase(t, h), 'knowledge-gaps'] as const,
  knowledgeGap: (t: string, h: string, id: string) =>
    [...hotelBase(t, h), 'knowledge-gap', id] as const,
  knowledgeSearch: (t: string, h: string, query: string, limit?: number) =>
    [...hotelBase(t, h), 'knowledge-search', { query, limit: limit ?? null }] as const,

  // operations / in-stay tasks (a "service request" is the canonical task)
  tasks: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'tasks', filters ?? null] as const,
  // Also matches taskExceptions (['tasks', 'exceptions', …]) — intended: a task
  // change can move a task in or out of the exceptions list.
  tasksPrefix: (t: string, h: string) => [...hotelBase(t, h), 'tasks'] as const,
  taskExceptions: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'tasks', 'exceptions', filters ?? null] as const,
  task: (t: string, h: string, id: string) => [...hotelBase(t, h), 'task', id] as const,
  roomStatus: (t: string, h: string, roomId: string) =>
    [...hotelBase(t, h), 'room', roomId, 'status'] as const,

  // onboarding
  onboardingRuns: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'onboarding', 'runs', filters ?? null] as const,
  onboardingRun: (t: string, h: string, runId: string) =>
    [...hotelBase(t, h), 'onboarding', 'run', runId] as const,
  onboardingDraftItems: (t: string, h: string, runId: string, filters?: Filters) =>
    [...hotelBase(t, h), 'onboarding', 'run', runId, 'draft-items', filters ?? null] as const,

  // scripts / voice config
  scriptVersions: (t: string, h: string, filters?: Filters) =>
    [...hotelBase(t, h), 'scripts', 'versions', filters ?? null] as const,
  // Prefix matching every (language, field) version query — used to invalidate
  // the whole per-field fan-out at once (e.g. after a fallback-language change).
  scriptVersionsPrefix: (t: string, h: string) =>
    [...hotelBase(t, h), 'scripts', 'versions'] as const,
  settings: (t: string, h: string) => [...hotelBase(t, h), 'settings'] as const,
  voiceConfig: (t: string, h: string) => [...hotelBase(t, h), 'voice-config'] as const,

  // phone
  phoneNumbers: (t: string, h: string) => [...hotelBase(t, h), 'phone-numbers'] as const,
  phoneNumberConfig: (t: string, h: string, phoneNumberId: string) =>
    [...hotelBase(t, h), 'phone-number', phoneNumberId, 'config'] as const,

  // single-hotel detail (the list lives at tenant level under `hotels`)
  hotel: (t: string, h: string) => [...hotelBase(t, h), 'hotel'] as const,

  // hotel setup — curated operational facts (check-in time, cancellation policy, …)
  hotelFacts: (t: string, h: string) => [...hotelBase(t, h), 'hotel-facts'] as const,

  // --- tenant-scoped ---
  hotels: (t: string) => ['tenant', t, 'hotels'] as const,
  members: (t: string) => ['tenant', t, 'members'] as const,
  integrations: (t: string) => ['tenant', t, 'integrations'] as const,
  integration: (t: string, integrationId: string) =>
    ['tenant', t, 'integration', integrationId] as const,
  integrationCapabilities: (t: string, integrationId: string) =>
    ['tenant', t, 'integration', integrationId, 'capabilities'] as const,
  usage: (t: string) => ['tenant', t, 'usage'] as const,
  callUsage: (t: string, callId: string) => ['tenant', t, 'call', callId, 'usage'] as const,
  callCost: (t: string, callId: string) => ['tenant', t, 'call', callId, 'usage', 'cost'] as const,
  audit: (t: string, filters?: Filters) => ['tenant', t, 'audit', filters ?? null] as const,

  // --- operator (cross-tenant, NOT tenant-scoped) ---
  operatorTenants: (filters?: Filters) => ['operator', 'tenants', filters ?? null] as const,
  operatorHotels: (filters?: Filters) => ['operator', 'hotels', filters ?? null] as const,
  operatorCalls: (filters?: Filters) => ['operator', 'calls', filters ?? null] as const,
  operatorUsers: (filters?: Filters) => ['operator', 'users', filters ?? null] as const,
  operatorTenant: (id: string) => ['operator', 'tenant', id] as const,
  operatorHotel: (id: string) => ['operator', 'hotel', id] as const,
  operatorTenantMembers: (tenantId: string) => ['operator', 'tenant', tenantId, 'members'] as const,
  operatorHotelFacts: (hotelId: string) => ['operator', 'hotel', hotelId, 'facts'] as const,
} as const;

/** Key prefix matching every query owned by a hotel — used on reconnect resync. */
export const hotelScopePrefix = (t: string, h: string) => hotelBase(t, h);
